import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { getFirebaseWebFirestore } from "./firebaseWeb";
import { getLiveActionResource } from "./liveActionApi";
import { checkNicknameAvailabilityViaApi, completeSignupProfileForCurrentUser } from "./signupProfileApi";

const NICKNAME_COLLECTION = "webUserNicknames";
const USER_PROFILE_COLLECTION = "webUserProfiles";
const USER_PRIVATE_PROFILE_COLLECTION = "webUserPrivateProfiles";
const USER_INTEGRITY_ALERT_COLLECTION = "webUserIntegrityAlerts";

function cleanEmail(value) {
  return String(value || "").toLocaleLowerCase("tr-TR");
}

function createNicknameError(message, code) {
  const error = new Error(message);
  error.code = code;
  return error;
}

function isPermissionError(error) {
  const code = String(error?.code || "").toLocaleLowerCase("tr-TR");
  const message = String(error?.message || "").toLocaleLowerCase("tr-TR");
  return code.includes("permission-denied") || message.includes("missing or insufficient permissions");
}

export function normalizeNickname(value) {
  return String(value || "")
    .trim()
    .replace(/^@+/, "")
    .toLocaleLowerCase("tr-TR");
}

export function validateNickname(value) {
  const nickname = normalizeNickname(value);
  if (nickname.length < 3) return { ok: false, nickname, message: "Kullanıcı adı en az 3 karakter olmalı." };
  if (nickname.length > 24) return { ok: false, nickname, message: "Kullanıcı adı en fazla 24 karakter olabilir." };
  if (!/^[a-z0-9._]+$/i.test(nickname)) return { ok: false, nickname, message: "Sadece harf, rakam, nokta ve alt çizgi kullanabilirsin." };
  return { ok: true, nickname, message: "Kullanıcı adı formatı uygun." };
}

export async function reportUserIntegrityAlert({ uid, email, type, severity = "warning", details = {} }) {
  if (!uid) return false;
  try {
    const db = getFirebaseWebFirestore();
    await addDoc(collection(db, USER_INTEGRITY_ALERT_COLLECTION), {
      uid,
      email: cleanEmail(email),
      type: String(type || "user.integrity.alert").slice(0, 80),
      severity: ["warning", "error", "critical"].includes(severity) ? severity : "warning",
      status: "open",
      details,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch {
    return false;
  }
}

export async function listUserIntegrityAlerts({ max = 30, status = "open" } = {}) {
  const db = getFirebaseWebFirestore();
  const safeLimit = Math.max(1, Math.min(Number(max) || 30, 100));
  const snapshot = await getDocs(query(
    collection(db, USER_INTEGRITY_ALERT_COLLECTION),
    orderBy("createdAt", "desc"),
    limit(safeLimit),
  ));
  return snapshot.docs
    .map((row) => ({ id: row.id, ...row.data() }))
    .filter((row) => !status || row.status === status);
}

export async function checkNicknameAvailability(value) {
  const validation = validateNickname(value);
  if (!validation.ok) return { available: false, ...validation };

  try {
    return await checkNicknameAvailabilityViaApi(validation.nickname);
  } catch {
    // Direct Firestore is kept for local emulator/dev environments.
  }

  const db = getFirebaseWebFirestore();
  const snapshot = await getDoc(doc(db, NICKNAME_COLLECTION, validation.nickname));
  return {
    ...validation,
    available: !snapshot.exists(),
    message: snapshot.exists() ? "Bu kullanıcı adı zaten kullanılıyor." : "Bu kullanıcı adı uygun.",
  };
}

export async function reserveNicknameForUser({ uid, email, nickname, legalConsent = null }) {
  const validation = validateNickname(nickname);
  if (!validation.ok) throw new Error(validation.message);
  if (!uid) throw createNicknameError("Kullanıcı adı kaydı için kullanıcı UID gerekli.", "nickname/missing-uid");

  try {
    return await completeSignupProfileForCurrentUser({ nickname: validation.nickname, legalConsent });
  } catch (error) {
    const canFallbackToFirestore = error?.status === 404 || error?.name === "TypeError";
    if (!canFallbackToFirestore) throw error;
  }

  const db = getFirebaseWebFirestore();
  const nicknameRef = doc(db, NICKNAME_COLLECTION, validation.nickname);
  const profileRef = doc(db, USER_PROFILE_COLLECTION, uid);
  const privateProfileRef = doc(db, USER_PRIVATE_PROFILE_COLLECTION, uid);

  await runTransaction(db, async (transaction) => {
    const nicknameSnapshot = await transaction.get(nicknameRef);
    if (nicknameSnapshot.exists() && nicknameSnapshot.data()?.uid !== uid) {
      throw new Error("Bu kullanıcı adı artık kullanılamıyor.");
    }

    transaction.set(nicknameRef, {
      uid,
      email: cleanEmail(email),
      nickname: validation.nickname,
      alias: validation.nickname,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    transaction.set(profileRef, {
      uid,
      nickname: validation.nickname,
      alias: validation.nickname,
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    }, { merge: true });

    transaction.set(privateProfileRef, {
      uid,
      email: cleanEmail(email),
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    }, { merge: true });
  });

  return { nickname: validation.nickname };
}

export async function getUserNicknameProfile(uid, options = {}) {
  if (!uid) throw createNicknameError("Kullanıcı adı profili okunamadı: kullanıcı UID eksik.", "nickname/missing-uid");

  let snapshot;
  try {
    const db = getFirebaseWebFirestore();
    snapshot = await getDoc(doc(db, USER_PROFILE_COLLECTION, uid));
  } catch (error) {
    const data = await getLiveActionResource("profile", { identifier: uid });
    if (data?.profile) return data.profile;
    throw error;
  }

  if (!snapshot.exists()) {
    await reportUserIntegrityAlert({
      uid,
      email: options.email,
      type: "nickname.profile_missing",
      severity: "error",
      details: { source: "getUserNicknameProfile" },
    });
    throw createNicknameError("Kullanıcı adı profili bulunamadı. Bu kullanıcı admin/moderatör kontrolüne alındı.", "nickname/profile-missing");
  }

  const profile = snapshot.data() || {};
  const nickname = normalizeNickname(profile.nickname);
  const validation = validateNickname(nickname);
  if (!validation.ok) {
    await reportUserIntegrityAlert({
      uid,
      email: options.email || profile.email,
      type: "nickname.profile_invalid",
      severity: "error",
      details: { source: "getUserNicknameProfile", message: validation.message },
    });
    throw createNicknameError("Kullanıcı adı profili geçersiz. Bu kullanıcı admin/moderatör kontrolüne alındı.", "nickname/profile-invalid");
  }

  try {
    const db = getFirebaseWebFirestore();
    const nicknameSnapshot = await getDoc(doc(db, NICKNAME_COLLECTION, nickname));
    if (!nicknameSnapshot.exists() || nicknameSnapshot.data()?.uid !== uid) {
      await reportUserIntegrityAlert({
        uid,
        email: options.email || profile.email,
        type: "nickname.reservation_missing",
        severity: "error",
        details: { source: "getUserNicknameProfile", nickname },
      });
      throw createNicknameError("Kullanıcı adı rezervasyonu doğrulanamadı. Bu kullanıcı admin/moderatör kontrolüne alındı.", "nickname/reservation-missing");
    }
  } catch (error) {
    if (!isPermissionError(error)) throw error;
  }

  return { ...profile, uid, nickname };
}

export async function upsertUserNicknameProfile({ uid, email, nickname }) {
  const validation = validateNickname(nickname);
  if (!uid) throw createNicknameError("Kullanıcı adı profili kaydedilemedi: kullanıcı UID eksik.", "nickname/missing-uid");
  if (!validation.ok) throw new Error(validation.message);

  const db = getFirebaseWebFirestore();
  const payload = {
    uid,
    nickname: validation.nickname,
    alias: validation.nickname,
    updatedAt: serverTimestamp(),
  };
  const nicknameRef = doc(db, NICKNAME_COLLECTION, validation.nickname);
  const profileRef = doc(db, USER_PROFILE_COLLECTION, uid);
  const privateProfileRef = doc(db, USER_PRIVATE_PROFILE_COLLECTION, uid);

  await runTransaction(db, async (transaction) => {
    const nicknameSnapshot = await transaction.get(nicknameRef);
    if (nicknameSnapshot.exists() && nicknameSnapshot.data()?.uid !== uid) {
      throw new Error("Bu kullanıcı adı artık kullanılamıyor.");
    }
    transaction.set(nicknameRef, {
      uid,
      email: cleanEmail(email),
      nickname: validation.nickname,
      alias: validation.nickname,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    transaction.set(profileRef, payload, { merge: true });
    transaction.set(privateProfileRef, {
      uid,
      email: cleanEmail(email),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  });
  return payload;
}
